import React, { createContext, useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLinkedPatients, LinkedPatient } from '../hooks/api/useLinkedPatients';
import { setCurrentPatientId } from '../lib/analytics';

/**
 * PatientContext の値の型定義
 */
export interface PatientContextValue {
  /** 現在選択中の患者ID */
  selectedPatientId: number | null;
  /** 現在選択中の患者インデックス */
  selectedPatientIndex: number;
  /** 現在選択中の患者オブジェクト */
  selectedPatient: LinkedPatient | undefined;
  /** 連携している患者リスト */
  linkedPatients: LinkedPatient[] | undefined;
  /** ロード中かどうか */
  isLoading: boolean;
  /** 患者IDで選択 */
  selectPatient: (patientId: number) => void;
  /** インデックスで選択 */
  selectPatientByIndex: (index: number) => void;
}

/**
 * PatientContext のデフォルト値
 */
const defaultValue: PatientContextValue = {
  selectedPatientId: null,
  selectedPatientIndex: 0,
  selectedPatient: undefined,
  linkedPatients: undefined,
  isLoading: true,
  selectPatient: () => {},
  selectPatientByIndex: () => {},
};

/**
 * 患者選択状態を管理するContext
 */
export const PatientContext = createContext<PatientContextValue>(defaultValue);

/**
 * PatientProvider Props
 */
interface PatientProviderProps {
  children: React.ReactNode;
}

/**
 * 患者選択状態を管理するProvider
 *
 * 機能:
 * - useLinkedPatients() の React Query データを内部で利用
 * - URLパラメータ (?patient_id=xxx) との同期
 * - 患者切り替え時に setCurrentPatientId() を自動呼び出し（GA4連携）
 */
export const PatientProvider: React.FC<PatientProviderProps> = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: linkedPatients, isLoading } = useLinkedPatients();

  // URLパラメータから患者インデックスを計算
  const selectedPatientIndex = useMemo(() => {
    const patientIdParam = searchParams.get('patient_id');
    if (patientIdParam && linkedPatients) {
      const patientId = parseInt(patientIdParam, 10);
      const index = linkedPatients.findIndex(p => p.patient_id === patientId);
      return index !== -1 ? index : 0;
    }
    return 0;
  }, [searchParams, linkedPatients]);

  // 現在選択中の患者
  const selectedPatient = linkedPatients?.[selectedPatientIndex];
  const selectedPatientId = selectedPatient?.patient_id ?? null;

  // patient_id 変更時に Analytics を更新
  useEffect(() => {
    setCurrentPatientId(selectedPatientId);
  }, [selectedPatientId]);

  // 患者IDで選択
  const selectPatient = useCallback((patientId: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('patient_id', patientId.toString());
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  // インデックスで選択
  const selectPatientByIndex = useCallback((index: number) => {
    const patient = linkedPatients?.[index];
    if (patient) {
      selectPatient(patient.patient_id);
    }
  }, [linkedPatients, selectPatient]);

  const value: PatientContextValue = useMemo(() => ({
    selectedPatientId,
    selectedPatientIndex,
    selectedPatient,
    linkedPatients,
    isLoading,
    selectPatient,
    selectPatientByIndex,
  }), [
    selectedPatientId,
    selectedPatientIndex,
    selectedPatient,
    linkedPatients,
    isLoading,
    selectPatient,
    selectPatientByIndex,
  ]);

  return (
    <PatientContext.Provider value={value}>
      {children}
    </PatientContext.Provider>
  );
};

export default PatientProvider;
